import { t as localeText } from "@/i18n/translate";
import type { MediaToolVariant } from "@/features/media-tools";
import { imageToolDefaults, imageToolFieldError } from "./imageToolsCatalog";
export type UpscaleMode = "precision" | "creative";
export const isTencentUpscaler = (variant: MediaToolVariant) => variant.settings.some(field => field.key === "type" && field.enum?.includes("fidelity"));
export function upscaleDefaults(variant: MediaToolVariant, mode: UpscaleMode) {
 const settings = imageToolDefaults(variant);
 if (settings.outscale === 1) settings.outscale = 2;
 if (isTencentUpscaler(variant)) settings.type = mode === "creative" ? "ultra" : "fidelity";
 return settings;
}
export function upscaleSettings(variant: MediaToolVariant, values: Record<string, unknown>, edges: boolean) {
 const keys = new Set(variant.settings.map(field => field.key));
 return Object.fromEntries(Object.entries(values).filter(([key,value]) => {
  if (!keys.has(key) || value === undefined || value === "") return false;
  if (key === "percent") return values.mode === "percent";
  if (["width","height","long_side","short_side"].includes(key)) return values.mode !== "percent" && (edges ? key.endsWith("side") : !key.endsWith("side"));
  if (key === "encode_quality") return values.encode_format !== "PNG";
  return true;
 }));
}
export function upscaleSize(settings: Record<string, unknown>, width: number, height: number) {
 let w = Number(settings.width || 0), h = Number(settings.height || 0);
 const long = Number(settings.long_side || 0), short = Number(settings.short_side || 0);
 if (long || short) [w,h] = width >= height ? [long,short] : [short,long];
 if (!settings.mode || settings.mode === "percent") { const scale = Number(settings.outscale ?? settings.percent ?? 2); w = width*scale; h = height*scale; }
 else if (settings.mode === "aspect") { const scale = Math.min(w ? w/width : Infinity,h ? h/height : Infinity); w=width*scale;h=height*scale; }
 return { width: Math.round(w), height: Math.round(h) };
}
export function upscaleError(variant: MediaToolVariant, settings: Record<string, unknown>, dimensions: {width?: number | null; height?: number | null}[]) {
 for (const field of variant.settings) { const error = imageToolFieldError(field,settings[field.key]); if (error) return `${field.label}: ${error}`; }
 if (settings.mode === "aspect" || settings.mode === "fixed") {
  const w = settings.width ?? settings.long_side, h = settings.height ?? settings.short_side;
  if (!w && !h) return localeText("interface.enter_at_least_one_target_dimension_365e8ad1", { defaultValue: "Enter at least one target dimension." });
  if (settings.mode === "fixed" && (!w || !h)) return localeText("interface.enter_both_target_dimensions_for_an_exact_size_241208cc", { defaultValue: "Enter both target dimensions for an exact size." });
  if (settings.long_side && Number(settings.short_side)>Number(settings.long_side)) return localeText("interface.the_long_edge_must_be_at_least_as_large_as_the_s_c0e2b393", { defaultValue: "The long edge must be at least as large as the short edge." });
 }
 for (const image of dimensions) {
  if (!image.width || !image.height) continue;
  const size = upscaleSize(settings,image.width,image.height);
  if (!Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width<1 || size.height<1 || size.width>8192 || size.height>8192 || size.width*size.height>40_000_000) return localeText("interface.choose_a_size_within_8192_pixels_per_edge_and_40_279b2cc5", { defaultValue: "Choose a size within 8192 pixels per edge and 40 megapixels." });
 }
}
